import React, { useEffect, useState, useCallback, useMemo } from 'react';
import useRealtimeRefresh, { eventPrefix } from '../../realtime/useRealtimeRefresh';
import { connect, useDispatch } from 'react-redux';
import { fetchRequest as fetchForecasts } from '../../redux/actions/forecasts/list';
import { triggerRequest as triggerForecast } from '../../redux/actions/forecasts/trigger';
import { fetchRequest as fetchPredictions } from '../../redux/actions/forecasts/predictions';
import { 
  Box, Typography, CircularProgress, Grid, Paper, Chip, 
  TablePagination, Tooltip, Card, CardContent, Link,
  Button, Divider, useTheme, IconButton, Dialog, 
  DialogTitle, DialogContent, DialogActions, FormControl,
  InputLabel, Select, MenuItem
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  CMButton, CMButtonOutlinedSmall
} from '../../containers/styledComponents';
import { InferencesIcon } from '../global/CustomIcons';
import TimelineIcon from '@mui/icons-material/Timeline';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TimerIcon from '@mui/icons-material/Timer';
import FilterListIcon from '@mui/icons-material/FilterList';
import { ResponsiveLine } from '@nivo/line';
import { useNavigate } from 'react-router-dom';
import { format } from '../../utils/datetime';
import { useTheme as useCustomTheme } from '../../theme/ThemeContext';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import MessageIcon from '@mui/icons-material/Message';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import HelpIcon from '@mui/icons-material/Help';

// Styled components
const ForecastCard = styled(Paper)(({ theme }) => ({
  minWidth: '100%',
  height: 'auto',
  mb: 1,
  p: 0.75,
  transition: 'box-shadow 0.3s ease',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 2px 4px rgba(0,0,0,0.4)' 
    : '0 1px 3px rgba(0,0,0,0.1)',
  '&:hover': {
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 4px 8px rgba(0,0,0,0.6)' 
      : '0 4px 8px rgba(0,0,0,0.15)'
  }
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 100,
  marginTop: theme.spacing(0.25),
}));

// Status Chip component
const StatusChip = ({ status }) => {
  let icon;
  let label;
  let color;
  
  switch (status) {
    case 'completed':
      icon = <CheckCircleIcon fontSize="inherit" />;
      label = 'Completed';
      color = 'success';
      break;
    case 'failed':
      icon = <ErrorIcon fontSize="inherit" />;
      label = 'Failed';
      color = 'error';
      break;
    case 'running':
      icon = <HourglassEmptyIcon fontSize="inherit" />;
      label = 'Running';
      color = 'warning';
      break;
    case 'pending':
      icon = <HourglassEmptyIcon fontSize="inherit" />;
      label = 'Pending';
      color = 'info';
      break;
    default:
      icon = <HelpIcon fontSize="inherit" />;
      label = status || 'Unknown';
      color = 'default';
  }
  
  return (
    <Chip
      icon={
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          fontSize: 10,
          ml: 0.25, 
          mr: -0.5
        }}>
          {icon}
        </Box>
      }
      label={label}
      size="small"
      color={color}
      sx={{
        height: 18,
        '& .MuiChip-label': {
          px: 1,
          py: 0,
          fontSize: '0.65rem',
          fontWeight: 500
        }
      }}
    />
  );
};

// Helper functions
const truncateId = (id) => {
  if (!id) return 'Unknown ID';
  return `${id.substring(0, 6)}...`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'Unknown date';
  try {
    return format(new Date(dateString), 'MMM d, HH:mm');
  } catch (e) {
    console.error('Date formatting error:', e);
    return 'Invalid date';
  }
};

// Chart component for forecast visualization
const ForecastChart = ({ data, isDarkMode }) => {
  const theme = useTheme();
  
  if (!data || data.length === 0) {
    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
          No forecast data available
        </Typography>
      </Box>
    );
  }

  // Format the data for the chart
  const chartData = data.map(series => ({
    id: series.label,
    data: series.data,
    color: getColorForSeries(series.label)
  }));

  // Function to get a unique color for each series based on its name
  function getColorForSeries(name) {
    const colors = [
      '#614dee', '#2f7d31', '#1976d2', '#ed6c02', '#9c27b0', 
      '#00838f', '#e91e63', '#827717', '#6d4c41'
    ];
    
    // Generate a simple hash for the name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = ((hash << 5) - hash) + name.charCodeAt(i);
      hash = hash & hash; // Convert to 32bit integer
    }
    
    // Use absolute value of hash to pick a color
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }

  // Custom tooltip component
  const CustomTooltip = ({ point }) => {
    return (
      <Box
        sx={{
          background: '#1a1a1a',
          color: 'white',
          padding: '12px 16px',
          border: '1px solid #374151',
          borderRadius: '8px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
          fontSize: '12px',
          minWidth: 160,
          zIndex: 1000,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '100%',
            left: '50%',
            marginLeft: '-5px',
            width: 0,
            height: 0,
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: '5px solid #1a1a1a'
          }
        }}
      >
        <Typography variant="caption" sx={{ 
          fontWeight: 600, 
          display: 'block', 
          mb: 1,
          color: point.serieColor || '#614dee',
          fontSize: '13px'
        }}>
          {point.serieId}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: 'white', fontSize: '12px' }}>
          <strong>Value:</strong> {typeof point.data.y === 'number' ? point.data.y.toFixed(2) : point.data.y}
        </Typography>
      </Box>
    );
  };

  return (
    <ResponsiveLine
      data={chartData}
      margin={{ top: 5, right: 55, bottom: 20, left: 35 }}
      xScale={{
        type: 'time',
        format: '%Y-%m-%dT%H:%M:%S.%LZ',
        useUTC: false,
        precision: 'minute'
      }}
      yScale={{
        type: 'linear',
        min: 'auto',
        max: 'auto',
        stacked: false,
        reverse: false
      }}
      axisBottom={{
        format: '%H:%M',
        tickValues: 3,
        tickSize: 2,
        tickPadding: 2,
        tickRotation: 0,
        legend: '',
        legendOffset: 0
      }}
      axisLeft={{
        tickSize: 2,
        tickPadding: 2,
        tickRotation: 0,
        legend: '',
        legendOffset: 0,
        tickValues: 3
      }}
      enableGridX={false}
      colors={{ scheme: 'category10' }}
      pointSize={2}
      pointColor={{ theme: 'background' }}
      pointBorderWidth={1}
      pointBorderColor={{ from: 'serieColor' }}
      useMesh={true}
      legends={[
        {
          anchor: 'top-right',
          direction: 'column',
          justify: false,
          translateX: 50,
          translateY: 0,
          itemsSpacing: 0,
          itemDirection: 'left-to-right',
          itemWidth: 50,
          itemHeight: 10,
          itemOpacity: 0.75,
          symbolSize: 6,
          symbolShape: 'circle',
          symbolBorderColor: 'rgba(0, 0, 0, .5)',
          effects: [
            {
              on: 'hover',
              style: {
                itemBackground: 'rgba(0, 0, 0, .03)',
                itemOpacity: 1
              }
            }
          ]
        }
      ]}
      tooltip={CustomTooltip}
      theme={{
        axis: {
          domain: {
            line: {
              stroke: theme.palette.text.secondary,
              strokeWidth: 1
            }
          },
          ticks: {
            line: {
              stroke: theme.palette.text.secondary,
              strokeWidth: 1
            },
            text: {
              fill: theme.palette.text.secondary,
              fontSize: 8
            }
          }
        },
        grid: {
          line: {
            stroke: theme.palette.divider,
            strokeWidth: 1
          }
        },
        legends: {
          text: {
            fill: theme.palette.text.primary,
            fontSize: 8
          }
        },
        tooltip: {
          container: {
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
            fontSize: 10
          }
        }
      }}
    />
  );
};

const ForecastsPage = ({ 
  fetchForecasts, 
  triggerForecast, 
  fetchPredictions,
  forecasts, 
  isLoading, 
  error, 
  pagination,
  predictionsData,
  isLoadingPredictions 
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isDarkMode } = useCustomTheme();
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedForecastId, setSelectedForecastId] = useState(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  const [modelFilter, setModelFilter] = useState('');
  const [loadedChartData, setLoadedChartData] = useState({});
  
  // State for trigger forecast dialog
  const [openTriggerDialog, setOpenTriggerDialog] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  
  // Reference to store interval ID for cleanup
  const isFirstRenderRef = React.useRef(true);
  
  // Load forecasts when parameters change
  useEffect(() => {
    fetchForecasts({ page, rowsPerPage, sortColumn, sortDirection, modelId: modelFilter || null });
    isFirstRenderRef.current = false;
  }, [fetchForecasts, page, rowsPerPage, sortColumn, sortDirection, modelFilter]);
  
  // Live (no polling): a new forecast is logged as an `inference.created`
  // event on the workspace channel — refetch the current page when one lands
  // (and on reconnect). Replaces the old 30s poll.
  useRealtimeRefresh(
    eventPrefix('inference.created'),
    () => {
      fetchForecasts({ page, rowsPerPage, sortColumn, sortDirection, modelId: modelFilter || null });
      setLastUpdateTime(new Date());
    },
  );
  
  // Load chart data for each forecast
  useEffect(() => {
    if (forecasts && forecasts.length > 0) {
      // Load prediction data for each forecast
      forecasts.forEach(forecast => {
        // Get the unique ID for this forecast
        const forecastId = forecast.uid;
        const segmentId = forecast.model_info?.uid;
        
        // Only fetch if we have a valid segment ID and haven't loaded this forecast's data yet
        if (segmentId && !loadedChartData[forecastId]) {
          fetchPredictions({ 
            segmentId,
            forecastId // Pass the forecast ID to identify this specific forecast
          });
        }
      });
      
      // Update available models for trigger dialog
      const uniqueModels = forecasts.reduce((models, forecast) => {
        if (forecast.model_id && forecast.model_info) {
          models[forecast.model_id] = forecast.model_info;
        }
        return models;
      }, {});
      
      setAvailableModels(Object.entries(uniqueModels).map(([id, info]) => ({
        id,
        name: info.uid || id,
        segment: info.segment_name || 'Unknown segment'
      })));
    }
  }, [forecasts, fetchPredictions, loadedChartData]);
  
  // Store prediction data when it's loaded
  useEffect(() => {
    if (predictionsData) {
      // Get the forecast ID from the data payload
      const forecastId = predictionsData.forecastId;
      
      if (forecastId) {
        // Store chart data indexed by forecast ID
        setLoadedChartData(prev => ({
          ...prev,
          [forecastId]: predictionsData.chartData || []
        }));
      }
    }
  }, [predictionsData]);
  
  // Reset chart data when page changes
  useEffect(() => {
    // Clear the loaded chart data when changing pages to force refetching
    setLoadedChartData({});
  }, [page, rowsPerPage, modelFilter]);
  
  // Handle page change
  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);
  
  // Handle rows per page change
  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);
  
  // Handle manual refresh
  const handleRefresh = useCallback(() => {
    fetchForecasts({ page, rowsPerPage, sortColumn, sortDirection, modelId: modelFilter || null });
    setLastUpdateTime(new Date());
  }, [fetchForecasts, page, rowsPerPage, sortColumn, sortDirection, modelFilter]);
  
  // Handle opening the trigger forecast dialog
  const handleOpenTriggerDialog = useCallback(() => {
    setOpenTriggerDialog(true);
  }, []);
  
  // Handle closing the trigger forecast dialog
  const handleCloseTriggerDialog = useCallback(() => {
    setOpenTriggerDialog(false);
  }, []);
  
  // Handle model selection in the dialog
  const handleModelChange = useCallback((event) => {
    setSelectedModelId(event.target.value);
  }, []);
  
  // Handle submitting the trigger forecast request
  const handleTriggerForecast = useCallback(() => {
    if (selectedModelId) {
      triggerForecast({ modelId: selectedModelId });
      setOpenTriggerDialog(false);
      // Reset the selected model
      setSelectedModelId('');
    }
  }, [triggerForecast, selectedModelId]);
  
  // Handle filter by model
  const handleFilterByModel = useCallback((modelId) => {
    setModelFilter(modelId === modelFilter ? '' : modelId);
    setPage(0);
  }, [modelFilter]);
  
  // Render forecast cards
  const forecastCards = useMemo(() => {
    if (!forecasts || forecasts.length === 0) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No forecasts available
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={1}>
        {forecasts.map((forecast) => {
          // Apply model filter if selected
          const modelMatches = !modelFilter || modelFilter === 'all' || forecast.model_id === modelFilter;
          if (!modelMatches) return null;
          
          // Set up IDs and variables
          const segmentId = forecast.model_info?.uid;
          const segmentName = forecast.model_info?.segment_name || 'Unknown segment';
          const forecastId = forecast.uid;
          const predictionsForForecast = loadedChartData[forecastId] || [];
          const isLoadingChartData = isLoadingPredictions;
          
          return (
            <Grid item xs={12} sm={6} md={4} key={forecastId} sx={{ mb: 0.75, p: 0.5 }}>
              <ForecastCard sx={{ p: 0.75 }}>
                {/* Header with ID and Status in a row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.7rem', mr: 0.5, whiteSpace: 'nowrap' }}>
                      {truncateId(forecastId)}
                    </Typography>
                    <Tooltip title={segmentName}>
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ 
                          fontSize: '0.65rem', 
                          maxWidth: '100px', 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {segmentName}
                      </Typography>
                    </Tooltip>
                  </Box>
                  <StatusChip status={forecast.status} />
                </Box>
                
                {/* Secondary info row with model and date */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center' }}>
                    <DeviceHubIcon sx={{ fontSize: 10, mr: 0.25 }} /> {forecast.model_id}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center' }}>
                    <AccessTimeIcon sx={{ fontSize: 10, mr: 0.25 }} /> {formatDate(forecast.created_at)}
                  </Typography>
                </Box>
                
                {/* Chart Section */}
                <ChartContainer>
                  {isLoadingChartData ? (
                    <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                      <CircularProgress size={16} thickness={4} />
                    </Box>
                  ) : predictionsForForecast && predictionsForForecast.length > 0 ? (
                    <ForecastChart data={predictionsForForecast} isDarkMode={theme.palette.mode === 'dark'} />
                  ) : (
                    <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                        No forecast data available
                      </Typography>
                    </Box>
                  )}
                </ChartContainer>
                
                {/* Message Section with icon */}
                {forecast.message && (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      mt: 0.25, 
                      maxHeight: '20px',
                      overflow: 'hidden'
                    }}
                  >
                    <MessageIcon sx={{ color: 'text.secondary', fontSize: 10, mt: 0.25, mr: 0.25 }} />
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ 
                        fontSize: '0.6rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {forecast.message}
                    </Typography>
                  </Box>
                )}
              </ForecastCard>
            </Grid>
          );
        })}
      </Grid>
    );
  }, [forecasts, loadedChartData, isLoadingPredictions, theme.palette.mode, modelFilter, handleFilterByModel]);
  
  // Show loading state
  if (isLoading && isFirstRenderRef.current) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Show error state
  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <ErrorIcon color="error" fontSize="large" />
        <Typography variant="h6" color="error" gutterBottom>Error Loading Forecasts</Typography>
        <Typography variant="body2">{error}</Typography>
      </Box>
    );
  }
  
  // Show empty state
  if (!forecasts || forecasts.length === 0) {
    return (
      <Box sx={{ p: 2, width: '100%' }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <InferencesIcon sx={{ mr: 1 }} />
          Forecasts
          <Box sx={{ flexGrow: 1 }} />
          <CMButton
            startIcon={<PlayArrowIcon />}
            onClick={handleOpenTriggerDialog}
          >
            Run Forecast
          </CMButton>
        </Typography>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <InfoIcon color="info" fontSize="large" sx={{ mb: 2, fontSize: '3rem' }} />
          <Typography variant="h6">No Forecasts Available</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            You haven't run any forecasts yet.
          </Typography>
          <CMButton
            startIcon={<PlayArrowIcon />}
            onClick={handleOpenTriggerDialog}
          >
            Run Your First Forecast
          </CMButton>
        </Paper>
        
        {/* Trigger Forecast Dialog */}
        <Dialog open={openTriggerDialog} onClose={handleCloseTriggerDialog}>
          <DialogTitle>Run New Forecast</DialogTitle>
          <DialogContent>
            <Box sx={{ minWidth: 300, mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Select Model</InputLabel>
                <Select
                  value={selectedModelId}
                  onChange={handleModelChange}
                  label="Select Model"
                >
                  {availableModels.map(model => (
                    <MenuItem key={model.id} value={model.id}>
                      {model.name} ({model.segment})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseTriggerDialog}>Cancel</Button>
            <CMButton 
              onClick={handleTriggerForecast}
              disabled={!selectedModelId}
            >
              Run Forecast
            </CMButton>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
  
  // Main content view
  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
        mb: 4
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 2,
          position: 'sticky',
          top: 0,
          zIndex: 2,
          py: 1,
          px: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
            <InferencesIcon sx={{ mr: 1 }} />
            Forecasts
          </Typography>
          
          {/* Model filter dropdown */}
          <FormControl sx={{ ml: 3, minWidth: 200 }} size="small">
            <InputLabel>Filter by Model</InputLabel>
            <Select
              value={modelFilter}
              onChange={(e) => setModelFilter(e.target.value)}
              label="Filter by Model"
              displayEmpty
            >
              <MenuItem value="">All Models</MenuItem>
              {availableModels.map(model => (
                <MenuItem key={model.id} value={model.id}>
                  {model.name} ({model.segment})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Last updated: {format(lastUpdateTime, 'h:mm:ss a')}
          </Typography>
          <CMButtonOutlinedSmall
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            size="small"
          >
            Refresh
          </CMButtonOutlinedSmall>
          <CMButton
            startIcon={<PlayArrowIcon />}
            onClick={handleOpenTriggerDialog}
            size="small"
          >
            Run Forecast
          </CMButton>
        </Box>
      </Box>
      
      {/* Forecast Cards */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%'
        }}
      >
        {forecastCards}
      </Box>
      
      {/* Pagination */}
      <Box
        sx={{
          mt: 2,
          width: '100%',
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <TablePagination
          component="div"
          count={pagination?.total_items || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Box>
      
      {/* Trigger Forecast Dialog */}
      <Dialog open={openTriggerDialog} onClose={handleCloseTriggerDialog}>
        <DialogTitle>Run New Forecast</DialogTitle>
        <DialogContent>
          <Box sx={{ minWidth: 300, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Select Model</InputLabel>
              <Select
                value={selectedModelId}
                onChange={handleModelChange}
                label="Select Model"
              >
                {availableModels.map(model => (
                  <MenuItem key={model.id} value={model.id}>
                    {model.name} ({model.segment})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTriggerDialog}>Cancel</Button>
          <CMButton 
            onClick={handleTriggerForecast}
            disabled={!selectedModelId}
          >
            Run Forecast
          </CMButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  forecasts: state.forecasts_list.response.forecasts,
  isLoading: state.forecasts_list.isLoading,
  error: state.forecasts_list.error,
  pagination: state.forecasts_list.response.pagination,
  predictionsData: state.forecasts_predictions.response,
  isLoadingPredictions: state.forecasts_predictions.isLoading
});

const mapDispatchToProps = (dispatch) => ({
  fetchForecasts: (params) => dispatch(fetchForecasts(params)),
  triggerForecast: (params) => dispatch(triggerForecast(params)),
  fetchPredictions: (params) => dispatch(fetchPredictions(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ForecastsPage);
